import { useEffect, useRef, useState } from 'react';
import {
  FundCriteria,
  FundScreenerDataType,
  FundScreenerSortingType,
  GetFundScreenerDataPayloadType,
} from '../../types/fundScreener.type';
import { getFundScreenerData } from '../../api/fundScreener.api';
import { toApiPeriod, DEFAULT_SCORE_VALUES } from '../../constants/fundScreener/fundCriteria.constant';
import { WidgetMessageType } from '../../utils/type';
import { WidgetMessage } from '../../types/common.type';

interface UseFundScreenerDataParams {
  market: string;
  token: string;
  page: number;
  pageSize: number;
  assetType: string;
  classification: string;
  criteria: FundCriteria[];
  sorting: FundScreenerSortingType;
  resetTrigger: number;
  onLoadingChange?: (loading: boolean) => void;
  onError?: (error: { message: string; type: string }) => void;
  onNoData?: (message: WidgetMessage) => void;
}

// Fixed columns map to fixed backend sort columns; dynamic criteria columns
// sort by their own apiField. Unknown falls back to NAME_FULL.
const FIXED_ORDER_COLUMNS: { [sortId: string]: string } = {
  name: 'NAME_FULL',
  code: 'ric',
  nav: 'NAV',
  tna: 'TNA',
};

const buildOrderByColumn = (sortId: string, criteria: FundCriteria[]): string => {
  if (FIXED_ORDER_COLUMNS[sortId]) return FIXED_ORDER_COLUMNS[sortId];
  const matched = criteria.find((item) => item.id === sortId && item.apiField);
  return matched?.apiField || 'NAME_FULL';
};

// Maps BE response field names to the table's (ticker->code, NAME_FULL->name,
// etc). `getFlagSvg` also needs 3-letter codes, not the 2-letter `market`.
const MARKET_TO_FLAG_CC: { [market: string]: string } = { AU: 'AUS', US: 'USA' };

const mapApiRow = (row: any): FundScreenerDataType => ({
  ...row,
  ric: row.ric,
  code: row.ticker,
  name: row.NAME_FULL,
  cc: MARKET_TO_FLAG_CC[row.market] || row.market,
  assetTypeLabel: row.assetType,
  nav: row.NAV,
  tna: row.TNA,
  currency: row.NAV_CCY || row.CURRENCY,
});

// Builds the /fund/screener/criteria/result payload. fundCurrency/fundCompany
// are plain filters (CURRENCY/mgmtCompanyName), excluded from the criteria map.
const buildPayload = ({
  market,
  page,
  pageSize,
  assetType,
  classification,
  criteria,
  sorting,
}: Pick<
  UseFundScreenerDataParams,
  'market' | 'page' | 'pageSize' | 'assetType' | 'classification' | 'criteria' | 'sorting'
>): GetFundScreenerDataPayloadType => {
  const selected = criteria.filter((item) => item.selected);
  const payload: GetFundScreenerDataPayloadType = {
    market,
    pageNo: page,
    pageSize,
    orderByColumn: buildOrderByColumn(sorting.id, selected),
    orderByDirection: sorting.asc ? 'ASC' : 'DESC',
    assetType: assetType !== 'ALL' ? [assetType] : [],
    classification: classification !== 'ALL' ? [classification] : [],
  };

  selected.forEach((item) => {
    if (item.id === 'fundCurrency') {
      payload.CURRENCY = item.currency;
      return;
    }
    if (item.id === 'fundCompany') {
      payload.mgmtCompanyName = item.companyName;
      return;
    }
    if (!item.apiField || item.supportsMinMax === false) return;

    if (item.controlType === 'score') {
      // BE rejects an empty `scores` array, so default to [3,4,5]. "NA" maps to `null`.
      const effectiveScores = item.scores ?? DEFAULT_SCORE_VALUES;
      const scores = effectiveScores.map((s) => (s === 'NA' ? null : s));
      const period = toApiPeriod(item.timeValue);
      payload[item.apiField] = { ...(period ? { period } : {}), scores };
      return;
    }

    const period = toApiPeriod(item.timeValue);
    payload[item.apiField] = {
      ...(period ? { period } : {}),
      from: item.selectedMin,
      to: item.selectedMax,
    };
  });

  return payload;
};

// Owns the screener results + isLoading, pushed out via onLoadingChange.
export const useFundScreenerData = ({
  market,
  token,
  page,
  pageSize,
  assetType,
  classification,
  criteria,
  sorting,
  resetTrigger,
  onLoadingChange,
  onError,
  onNoData,
}: UseFundScreenerDataParams) => {
  const [data, setData] = useState<FundScreenerDataType[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmptyData, setIsEmptyData] = useState(false);

  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);

  const selectedCriteria = criteria.filter((item) => item.selected);
  const isReadyForResult =
    !selectedCriteria.some((item) => item.pendingMinMax || item.minMaxUnavailable) &&
    selectedCriteria
      .filter((item) => item.controlType === 'range' && item.supportsMinMax !== false)
      .every((item) => item.selectedMin !== undefined && item.selectedMax !== undefined);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isReadyForResult) return;

      setIsLoading(true);
      setIsEmptyData(false);

      const payload = buildPayload({
        market,
        page,
        pageSize,
        assetType,
        classification,
        criteria,
        sorting,
      });

      getFundScreenerData(payload, token)
        .then((response) => {
          setData(response.datalist.map(mapApiRow));
          setTotal(response.totalCount);
          setIsLoading(false);
          setIsEmptyData(response.totalCount === 0);
          if (response.totalCount === 0) {
            onNoData?.({
              type: WidgetMessageType.NoData,
              source: 'FundScreenerWidget',
              data: null,
            });
          }
        })
        .catch((error: any) => {
          setIsLoading(false);
          setIsEmptyData(true);
          onError?.({
            message: error.message,
            type: `${error.name}_${error.code}`,
          });
        });
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    market,
    criteria,
    assetType,
    classification,
    sorting,
    resetTrigger,
    token,
    isReadyForResult,
  ]);

  return { data, total, isLoading, isEmptyData };
};
